import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api, { formatRelative, formatApiError } from "../lib/api";
import Layout from "../components/Layout";
import { useAuth } from "../contexts/AuthContext";
import { MapPin, Eye, MessageCircle, Pencil, Trash2, Lock, Rocket } from "lucide-react";
import CountryFlag from "../components/CountryFlag";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function AnnonseDetalj() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [ad, setAd] = useState(null);
  const [error, setError] = useState("");
  const [activeImg, setActiveImg] = useState(0);

  const load = () =>
    api.get(`/ads/${id}`).then((r) => setAd(r.data)).catch((e) => setError(formatApiError(e)));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const remove = async () => {
    if (!window.confirm("Slett denne annonsen?")) return;
    try { await api.delete(`/ads/${id}`); nav("/annonser"); }
    catch (e) { setError(formatApiError(e)); }
  };

  const boost = async () => {
    const msg = `Boost denne annonsen?\n\nDen pinnes til toppen av kategorien i ${ad.boost_duration_hours || 24} timer og får 🚀-merke.\nKoster ${ad.boost_cost_tokens || 10} tokens.`;
    if (!window.confirm(msg)) return;
    try {
      const r = await api.post(`/ads/${id}/boost`);
      alert(`Annonsen er boostet til ${new Date(r.data.boosted_until).toLocaleString("nb-NO")}. Du har ${r.data.tokens_left} tokens igjen.`);
      load();
    } catch (e) { setError(formatApiError(e)); }
  };

  if (error) return <Layout withSidebar={false}><div className="text-[var(--magenta)]">{error}</div></Layout>;
  if (!ad) return <Layout withSidebar={false}><div className="font-fraunces italic">Laster …</div></Layout>;

  const imgs = ad.image_paths || [];

  return (
    <Layout withSidebar={false}>
      <div className="font-meta mb-3">
        <Link to="/annonser" className="hover:text-[var(--cream)]">Annonser</Link>
        <span className="mx-2">/</span>
        <Link to={`/annonser/${ad.category}`} className="hover:text-[var(--cream)]">{ad.category_label}</Link>
        <span className="mx-2">/</span>
        <Link to={`/annonser/${ad.category}/${ad.subcategory}`} className="hover:text-[var(--cream)]">{ad.subcategory_label}</Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-8">
        {/* Bilder + body */}
        <div>
          {imgs.length > 0 ? (
            <div className="space-y-2">
              <div className="aspect-[4/3] bg-black flex items-center justify-center overflow-hidden border border-white/10">
                <img src={`${BACKEND}/api/ads/images/raw?path=${encodeURIComponent(imgs[activeImg])}`}
                     alt={ad.title} className="w-full h-full object-contain" data-testid="ad-main-image" />
              </div>
              {imgs.length > 1 && (
                <div className="flex gap-2 flex-wrap">
                  {imgs.map((p, i) => (
                    <button key={p} onClick={() => setActiveImg(i)}
                            className={`w-20 aspect-square bg-black border ${i === activeImg ? "border-[var(--magenta)]" : "border-white/10"}`}>
                      <img src={`${BACKEND}/api/ads/images/raw?path=${encodeURIComponent(p)}`}
                           alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="aspect-[4/3] bg-black flex items-center justify-center border border-white/10 font-fraunces italic text-[var(--cream-faint)]">
              Ingen bilder
            </div>
          )}
          <div className="mt-6">
            <h1 className="font-fraunces italic text-3xl sm:text-4xl text-[var(--cream)] leading-tight">{ad.title}</h1>
            {ad.price != null && (
              <div className="font-fraunces italic text-3xl text-[var(--magenta)] mt-2">{ad.price} kr</div>
            )}
            <p className="font-manrope text-[var(--cream)] mt-6 whitespace-pre-wrap leading-relaxed">{ad.body}</p>
            {(ad.tags || []).length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-6">
                {ad.tags.map((t) => (
                  <span key={t} className="font-meta text-[10px] px-2 py-0.5 border border-white/15">#{t}</span>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Side-kort: owner + handlinger */}
        <aside className="space-y-4">
          <div className="border border-white/10 bg-[var(--noir-elev)]/40 p-5">
            <Link to={`/profile/${ad.owner?.username}`} className="font-fraunces italic text-2xl text-[var(--cream)] hover:text-[var(--magenta)]">
              {ad.owner?.username || "ukjent"}
            </Link>
            {(ad.owner?.is_verified || ad.owner?.tier === "vip" || ad.owner?.tier === "creator") && (
              <div className="font-meta text-[var(--cognac)] mt-1 inline-flex items-center gap-1"><Lock className="h-2.5 w-2.5" /> verifisert</div>
            )}
            <div className="font-meta mt-3 flex flex-col gap-1.5">
              {(ad.city || ad.fylke || ad.country) && (
                <div className="inline-flex items-center gap-1.5">
                  <MapPin className="h-3 w-3" />
                  {ad.country && <CountryFlag iso={ad.country} size="sm" data-testid="ad-flag" />}
                  {[ad.city, ad.fylke].filter(Boolean).join(", ")}
                </div>
              )}
              <div><Eye className="h-3 w-3 inline mr-1.5" />{ad.view_count} visninger · publisert {formatRelative(ad.created_at)}</div>
            </div>
            {ad.owner?.username && user?.username !== ad.owner.username && (
              <Link to={`/meldinger?til=${ad.owner.username}`} className="btn-magenta w-full mt-4 justify-center inline-flex items-center" data-testid="contact-btn">
                <MessageCircle className="h-3.5 w-3.5 mr-2" /> Kontakt annonsør
              </Link>
            )}
            {ad.can_edit && (
              <>
                <button onClick={boost}
                        className={`w-full mt-3 text-xs inline-flex items-center justify-center gap-1.5 px-3 py-2 border rounded-sm transition-colors ${
                          ad.is_boosted
                            ? "border-[var(--magenta)] bg-[var(--magenta)]/15 text-[var(--magenta)] hover:bg-[var(--magenta)] hover:text-black"
                            : "border-[var(--magenta)]/50 text-[var(--magenta)] hover:bg-[var(--magenta)] hover:text-black hover:border-[var(--magenta)]"
                        }`}
                        data-testid="boost-btn">
                  <Rocket className="h-3.5 w-3.5" />
                  {ad.is_boosted
                    ? `Boost forleng (+${ad.boost_duration_hours || 24}t, ${ad.boost_cost_tokens || 10} tokens)`
                    : `Boost annonsen (${ad.boost_duration_hours || 24}t, ${ad.boost_cost_tokens || 10} tokens)`}
                </button>
                <div className="mt-3 flex gap-2">
                  <Link to={`/annonser/rediger/${ad.id}`} className="btn-ghost text-xs flex-1 inline-flex items-center justify-center gap-1.5">
                    <Pencil className="h-3 w-3" /> Rediger
                  </Link>
                  <button onClick={remove} className="btn-ghost text-xs flex-1 inline-flex items-center justify-center gap-1.5 hover:text-[var(--magenta)]" data-testid="delete-btn">
                    <Trash2 className="h-3 w-3" /> Slett
                  </button>
                </div>
              </>
            )}
          </div>
        </aside>
      </div>
    </Layout>
  );
}
